// Short farmer-facing names for the soil classes shown on the map legend.
//
// HAND-WRITTEN. Not generated: `soilClassColours.js` next to this is generated
// and will be overwritten, this file will not.
//
// Written 20/08/2026 and confirmed 11/09/2026. The provider's full class name is
// never lost, so a later change costs nothing but the words.
//
// Why shorten at all: Soilscapes class names are written for soil scientists.
// "Slowly permeable seasonally wet slightly acid but base-rich loamy and clayey
// soils" is one real class, and it is eleven words on a legend beside a colour
// swatch on a phone.
//
// Nothing is lost by shortening here, and that is the reason this is safe: the
// Zone Details land characteristics row still carries the provider's full class
// name AND a "Learn more about this soil type" link to their own description
// page for that exact class, built from SS_ID. So the legend answers "which
// colour is which" and the panel answers "what does that mean".
//
// What each name tries to keep, because these are what separate one class from
// the next in the same field: drainage, texture, and lime or acidity.
//
// Scotland's Hutton classes are deliberately absent. They are already one to
// three words, so the length problem does not exist there, and turning "podzol"
// or "gley" into plain English is a soil-science judgement rather than a copy
// one.

export const SOIL_CLASS_SHORT_NAMES = {
  // Peat
  'Blanket bog peat soils': 'Blanket bog peat',
  'Raised bog peat soils': 'Raised bog peat',
  'Fen peat soils': 'Fen peat',

  // Free-draining
  'Freely draining acid loamy soils over rock': 'Free-draining acid loam over rock',
  'Freely draining floodplain soils': 'Free-draining floodplain',
  'Freely draining lime-rich loamy soils': 'Free-draining lime-rich loam',
  'Freely draining sandy Breckland soils': 'Free-draining Breckland sand',
  'Freely draining slightly acid but base-rich soils': 'Free-draining base-rich soil',
  'Freely draining slightly acid loamy soils': 'Free-draining slightly acid loam',
  'Freely draining slightly acid sandy soils': 'Free-draining slightly acid sand',
  'Freely draining very acid sandy and loamy soils': 'Free-draining very acid sand and loam',

  // Impeded or slow drainage. "Poor drainage" rather than "impeded", and
  // "seasonally wet" kept because it is the practical difference: wet in winter
  // and workable in summer is not the same field as wet all year.
  'Lime-rich loamy and clayey soils with impeded drainage': 'Lime-rich loam and clay, poor drainage',
  'Slightly acid loamy and clayey soils with impeded drainage': 'Slightly acid loam and clay, poor drainage',
  'Slowly permeable seasonally wet acid loamy and clayey soils': 'Seasonally wet acid loam and clay',
  'Slowly permeable seasonally wet slightly acid but base-rich loamy and clayey soils':
    'Seasonally wet base-rich loam and clay',

  // Naturally high groundwater. "Wet" carries it; "naturally high groundwater"
  // is the mechanism, not the thing a farmer needs off a legend.
  'Loamy and clayey floodplain soils with naturally high groundwater': 'Wet floodplain loam and clay',
  'Loamy and clayey soils of coastal flats with naturally high groundwater': 'Wet coastal loam and clay',
  'Loamy and sandy soils with naturally high groundwater and a peaty surface':
    'Wet loam and sand, peaty surface',
  'Loamy soils with naturally high groundwater': 'Wet loam',
  'Naturally wet very acid sandy and loamy soils': 'Wet very acid sand and loam',

  // Upland
  'Slowly permeable wet very acid upland soils with a peaty surface': 'Wet upland soil, peaty surface',
  'Very acid loamy upland soils with a wet peaty surface': 'Very acid upland loam, peaty surface',

  // Shallow over rock
  'Shallow lime-rich soils over chalk or limestone': 'Shallow soil over chalk or limestone',
  'Shallow very acid peaty soils over rock': 'Shallow peaty soil over rock',

  // Coastal and other
  'Saltmarsh soils': 'Saltmarsh',
  'Sand dune soils': 'Sand dune',
  'Restored soils mostly from quarry and opencast spoil': 'Restored quarry ground',
  'Unsurveyed/Urban': 'Not surveyed or built up',
  'Water body': 'Water',
};

/**
 * Short name for a soil class, falling back to the provider's own string.
 *
 * Falling back rather than throwing is deliberate: if a provider adds or renames
 * a class, the legend shows their long name, which is ugly and correct, instead
 * of an empty row. The test suite catches the drift separately.
 */
export function shortSoilClassName(value) {
  if (value == null) return '';
  return SOIL_CLASS_SHORT_NAMES[String(value)] || String(value);
}
